import invariant from 'invariant';

import { apiServerFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import type { EmbedSearchWidgetLayoutHandleProps } from '#/modules/embedSearchWidget/layout/EmbedSearchWidgetLayoutHandleProps';
import type { EmbedSearchWidgetAlgoliaImportedVolopHit } from '#/modules/embedSearchWidget/volops/algolia/EmbedSearchWidgetAlgoliaImportedVolopHit';
import { embedSearchWidgetApiServerFetchVolop } from '#/modules/embedSearchWidget/volops/api/embedSearchWidgetApiFetchVolop';
import type { embedSearchWidgetViewVolopRoute } from '#/modules/embedSearchWidget/volops/routing/embedSearchWidgetVolops.routes';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import { searchAlgoliaVolopsIndex } from '#/modules/search/algolia/searchAlgoliaVolopsIndex';
import { ClientOnlyLazySearchVolopsRefinements } from '#/modules/search/volops/components/Refinements/ClientOnlyLazySearchVolopsRefinements';
import { LazyNotFoundPage } from '#/pages/NotFound/LazyNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { algoliaGetObject } from '#/utils/algolia/algoliaGetObject';

import { EmbedSearchWidgetViewVolopPage } from './EmbedSearchWidgetViewVolopPage';
import { EmbedSearchWidgetViewImportedVolopPage } from './Imported/EmbedSearchWidgetViewImportedVolopPage';

export const handle = {
  searchRefinements: (
    <ClientOnlyLazySearchVolopsRefinements includeSourcesFacet />
  ),
  isSearchPage: false,
} satisfies EmbedSearchWidgetLayoutHandleProps;

export async function loader({
  request,
  context,
  params: { embedSearchWidgetId, volopId },
}: HorizonAppRouteLoaderArgs<typeof embedSearchWidgetViewVolopRoute>) {
  const { serverContext } = getServerRouterContext(context);

  if (volopId.startsWith('GOLDEN')) {
    const { appConfig } = await apiServerFetchUserEnvironment({
      request,
      context,
    })();

    const importedVolopHit =
      await algoliaGetObject<EmbedSearchWidgetAlgoliaImportedVolopHit>({
        config: getSearchAlgoliaDefaultConfig({ appConfig }),
        index: searchAlgoliaVolopsIndex({ sort: 'relevance', appConfig }),
        id: volopId,
        attributes: ['*'],
        isUsingAlgoliaMock: appConfig.algolia.mock,
      }).catch(() => null);

    if (!importedVolopHit) {
      return idealistNotFound(null);
    }

    return { type: 'IMPORTED', importedVolopHit } as const;
  }

  try {
    const volop = await embedSearchWidgetApiServerFetchVolop({
      request,
      context,
    })(embedSearchWidgetId, volopId);

    if (volop.type === 'UNPUBLISHED_VOLOP') {
      return idealistNotFound(null);
    }

    return {
      type: 'VOLOP',
      volop,
      now: serverContext.now,
    } as const;
  } catch {
    return idealistNotFound(null);
  }
}

export default function EmbedSearchWidgetViewVolopHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { embedSearchWidget } = useEmbedSearchWidget();
  invariant(embedSearchWidget, 'Embed search widget not set.');

  switch (loaderData?.type) {
    case undefined:
      return <LazyNotFoundPage />;
    case 'IMPORTED':
      return (
        <RouteModule title={loaderData.importedVolopHit.name}>
          <EmbedSearchWidgetViewImportedVolopPage
            importedVolopHit={loaderData.importedVolopHit}
          />
        </RouteModule>
      );
    case 'VOLOP':
      return (
        <RouteModule title={loaderData.volop.name}>
          <EmbedSearchWidgetViewVolopPage
            volop={loaderData.volop}
            embedSearchWidget={embedSearchWidget}
            now={loaderData.now}
          />
        </RouteModule>
      );
  }
}
